// User Group Form
// Create and edit user groups

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2 } from 'lucide-react';
import { createDefaultUserGroup, createGroupMember, validateUserGroup } from '../../../types/userGroup';
import { useUserGroupMutations } from '../../../hooks/useUserGroups';
import { useGroupTypes, useShiftTypes, useWorkloadBalancingMethods } from '../../../hooks/useTaskNomenclatures';
import { departmentService } from '../../../services/firebaseService';
import { userService } from '../../../services/firestoreService';
import './UserGroupForm.css';

const UserGroupForm = ({ group, userId, onSave, onCancel }) => {
  const { t } = useTranslation('userManagement');
  const [formData, setFormData] = useState(group || createDefaultUserGroup());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);

  const { createGroup, updateGroup } = useUserGroupMutations();
  const { items: groupTypes } = useGroupTypes();
  const { items: shiftTypes } = useShiftTypes();
  const { items: balancingMethods } = useWorkloadBalancingMethods();

  // Load departments and users
  useEffect(() => {
    const loadData = async () => {
      try {
        const [depts, usersData] = await Promise.all([
          departmentService.getAll(),
          userService.getAll()
        ]);
        setDepartments(depts || []);
        setUsers(usersData || []);
        console.log('✅ Loaded departments:', depts?.length || 0);
        console.log('✅ Loaded users:', usersData?.length || 0);
      } catch (error) {
        console.error('❌ Error loading data:', error);
      }
    };
    loadData();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleAddMember = () => {
    const newMember = createGroupMember('', '', '', true);
    setFormData(prev => ({
      ...prev,
      members: [...prev.members, newMember]
    }));
  };

  const handleRemoveMember = (index) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.filter((_, i) => i !== index)
    }));
  };

  const handleMemberChange = (index, field, value) => {
    setFormData(prev => {
      const updatedMembers = [...prev.members];
      updatedMembers[index] = {
        ...updatedMembers[index],
        [field]: value
      };
      
      // If userId changed, update userName
      if (field === 'userId' && users) {
        const selectedUser = users.find(u => u.id === value);
        if (selectedUser) {
          updatedMembers[index].userName = `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() || selectedUser.email;
          updatedMembers[index].role = selectedUser.role_id || 'User';
        }
      }
      
      return {
        ...prev,
        members: updatedMembers
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validation = validateUserGroup(formData);
    if (!validation.valid) {
      const errorObj = {};
      validation.errors.forEach(err => {
        errorObj.general = (errorObj.general || '') + err + '. ';
      });
      setErrors(errorObj);
      return;
    }

    try {
      setSaving(true);
      setErrors({});

      if (group && group.id) {
        await updateGroup(group.id, formData, userId);
      } else {
        await createGroup(formData, userId);
      }

      onSave();
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="user-group-form">
      <div className="form-header">
        <div>
          <h2>{group ? t('editUserGroup') : t('createUserGroup')}</h2>
          <p className="subtitle">{t('configureUserGroupSubtitle')}</p>
        </div>
        <button className="btn btn-secondary" onClick={onCancel}>
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="group-form-content">
        {errors.general && (
          <div className="error-message">{errors.general}</div>
        )}

        {/* Basic Information */}
        <div className="form-section">
          <h3>{t('basicInformation')}</h3>
          
          <div className="form-grid">
            <div className="form-group full-width">
              <label>{t('groupNameRequired')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={t('groupNamePlaceholder')}
                required
              />
            </div>

            <div className="form-group full-width">
              <label>{t('groupDescription')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('descriptionPlaceholder')}
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>{t('groupType')} *</label>
              <select
                value={formData.groupType}
                onChange={(e) => handleChange('groupType', e.target.value)}
                required
              >
                <option value="">{t('selectGroupType')}</option>
                {groupTypes && groupTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('groupDepartment')}</label>
              <select
                value={formData.departmentId || ''}
                onChange={(e) => {
                  const dept = departments.find(d => d.id === e.target.value);
                  handleChange('departmentId', e.target.value);
                  handleChange('departmentName', dept?.name || null);
                }}
              >
                <option value="">{t('allDepartments')}</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('shiftType')}</label>
              <select
                value={formData.shiftType || ''}
                onChange={(e) => handleChange('shiftType', e.target.value)}
              >
                <option value="">{t('allShiftsOption')}</option>
                {shiftTypes && shiftTypes.map(shift => (
                  <option key={shift.id} value={shift.id}>{shift.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('status')}</label>
              <select
                value={formData.isActive ? 'active' : 'inactive'}
                onChange={(e) => handleChange('isActive', e.target.value === 'active')}
              >
                <option value="active">{t('active')}</option>
                <option value="inactive">{t('inactive')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Members */}
        <div className="form-section">
          <div className="section-header">
            <h3>{t('members')}</h3>
            <button type="button" className="btn btn-secondary" onClick={handleAddMember}>
              <Plus size={16} /> {t('addMember')}
            </button>
          </div>

          {formData.members.length === 0 ? (
            <div className="empty-hint">
              {t('noMembersHint')}
            </div>
          ) : (
            <div className="members-list-editor">
              {formData.members.map((member, index) => (
                <div key={index} className="member-item">
                  <div className="member-fields">
                    <div className="form-group">
                      <label>{t('personnel')}</label>
                      <select
                        value={member.userId}
                        onChange={(e) => handleMemberChange(index, 'userId', e.target.value)}
                        required
                      >
                        <option value="">{t('selectPersonnel')}</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email} - {user.role_id || 'User'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={member.isPrimary}
                          onChange={(e) => handleMemberChange(index, 'isPrimary', e.target.checked)}
                        />
                        {' '}{t('primaryMember')}
                      </label>
                    </div>

                    <button
                      type="button"
                      className="delete-btn-small"
                      onClick={() => handleRemoveMember(index)}
                      title="Remove Member"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assignment Rules */}
        <div className="form-section">
          <h3>{t('assignmentRules')}</h3>
          
          <div className="form-grid">
            <div className="form-group">
              <label>{t('maxConcurrentTasks')}</label>
              <input
                type="number"
                value={formData.assignmentRules.maxConcurrentTasks}
                onChange={(e) => handleNestedChange('assignmentRules', 'maxConcurrentTasks', parseInt(e.target.value))}
                min="1"
              />
            </div>

            <div className="form-group">
              <label>{t('maxDailyTasks')}</label>
              <input
                type="number"
                value={formData.assignmentRules.maxDailyTasks}
                onChange={(e) => handleNestedChange('assignmentRules', 'maxDailyTasks', parseInt(e.target.value))}
                min="1"
              />
            </div>

            <div className="form-group">
              <label>{t('workloadBalancing')}</label>
              <select
                value={formData.assignmentRules.workloadBalancing}
                onChange={(e) => handleNestedChange('assignmentRules', 'workloadBalancing', e.target.value)}
              >
                {balancingMethods && balancingMethods.map(method => (
                  <option key={method.id} value={method.id}>{method.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.assignmentRules.autoAssignment}
                  onChange={(e) => handleNestedChange('assignmentRules', 'autoAssignment', e.target.checked)}
                />
                {' '}Auto Assignment
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-cancel" onClick={onCancel}>
            {t('cancelGroup')}
          </button>
          <button type="submit" className="btn btn-save" disabled={saving}>
            {saving ? t('saving') : (group ? t('updateGroup') : t('createGroupButton'))}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserGroupForm;

